import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, Briefcase, X, Upload, ChevronDown, ChevronUp } from "lucide-react";
import useLang from "../../hooks/useLang";

const JobCard = ({ job, index = 0 }) => {
  const { t, isRTL, dir } = useLang();
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const title = isRTL ? job.titleAr : job.title;
  const department = isRTL ? job.departmentAr : job.department;
  const location = isRTL ? job.locationAr : job.location;
  const type = isRTL ? job.typeAr : job.type;
  const description = isRTL ? job.descriptionAr : job.description;
  const requirements = isRTL ? job.requirementsAr : job.requirements;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setModalOpen(false);
        setSubmitted(false);
      }, 2000);
    }, 1500);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1, duration: 0.5 }}
        className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs bg-accent/10 text-primary font-medium px-2.5 py-0.5 rounded-full">
                  {department}
                </span>
                <span
                  className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                    type.includes("Full") || type.includes("دوام كامل")
                      ? "bg-green-50 text-green-700"
                      : "bg-blue-50 text-blue-700"
                  }`}
                >
                  {type}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 text-lg mb-3">{title}</h3>
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-accent" /> {location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={14} className="text-accent" /> {type}
                </span>
                <span className="flex items-center gap-1.5">
                  <Briefcase size={14} className="text-accent" /> {department}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={() => setModalOpen(true)}
              className="bg-accent text-primary px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-accent/90 transition-colors"
            >
              {t("careers.applyNow")}
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors"
            >
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {expanded ? "Less" : "Details"}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-gray-100 px-6 pb-6 pt-4"
            >
              <p className="text-gray-600 text-sm mb-4">{description}</p>
              <h4 className="font-medium text-gray-800 text-sm mb-2">{t("careers.requirements")}</h4>
              <div className="flex flex-wrap gap-2">
                {requirements.map((req) => (
                  <span
                    key={req}
                    className="text-xs bg-primary/5 text-primary px-3 py-1 rounded-full border border-primary/10"
                  >
                    {req}
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Application Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              dir={dir}
            >
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">{t("careers.applyFor")}</p>
                  <h2 className="font-bold text-gray-900">{title}</h2>
                </div>
                <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              {submitted ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">✓</span>
                  </div>
                  <p className="text-gray-700 font-medium">Application submitted!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("careers.yourName")} *
                    </label>
                    <input
                      required
                      type="text"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("careers.yourEmail")} *
                    </label>
                    <input
                      required
                      type="email"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("careers.uploadCV")} *
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer border-2 border-dashed border-gray-200 rounded-xl px-4 py-4 hover:border-accent/50 transition-colors">
                      <Upload size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-400">Upload PDF, max 5MB</span>
                      <input required type="file" accept=".pdf" className="hidden" />
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("careers.coverLetter")}
                    </label>
                    <textarea
                      rows={4}
                      placeholder={t("careers.coverPlaceholder")}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-accent text-primary font-semibold py-3 rounded-xl hover:bg-accent/90 transition-colors disabled:opacity-60"
                  >
                    {submitting ? t("careers.submitting") : t("careers.submit")}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default JobCard;
